import React from 'react';
import { observer } from 'mobx-react';
import { classes, style } from 'typestyle';
import { ScrollableOverlay } from '../../scrollable-overlay/scrollable-overlay';
import { Component, deps, inject } from '../../../lib/component';
import { colorContrast100, colorGunmetal, rebrand } from '../../../theme/color';
import { TrackSchemaWithUsage } from '../../../modules/page/checkout/model';
import { Loading } from '../../loading/loading';
import { isEmpty } from '../utilities/objects';
import { centerCenter, flexRoot, horizontal, wrap } from 'csstips/lib/flex';
import { horizontallySpaced } from 'csstips/lib/box';
import { ArrowRight } from '../atoms/icons/ArrowRight';
import { Person } from '../atoms/icons/person';
import { Album } from '../atoms/icons/Album';
import { Description } from '../atoms/icons/Discription';
import { percent, px } from 'csx';
import { Link } from 'react-router';
import { TrackSchema } from '../../../types/schema';
import maybe from '../../../lib/maybe';
import overlayStyles from '../../../styles/scrollable-overlay';
import { getBpmHref, getGenreOrMoodHref, getTempoHref, getTempoRange } from '../utilities/search';

import { zModal } from '../../../theme/z';
import { ArtistLinkList } from '../../artist-link-list';
import { resetAnchorStyles } from '../../../theme/reset';
import { VideoPlatform } from '../molecules/VideoPlatform';
import { spacing } from '../../../theme/spacing';
import { fontstackDefault } from '../config';
import { TagButton } from '../atoms/button/TagButton';
import { borderRadius } from '../../../theme/border';
import { ClaimsManagedStatus } from '../molecules/ClaimsManagedStatus';
import { TrackFeatureCard } from '../molecules/TrackFeatureCard';
import { NoVocal } from '../atoms/icons/NoVocalInstrumental';

export interface SongInfoModalProps {
  show: boolean;
  track: TrackSchemaWithUsage;
  onRequestClose(): void;
}
@inject(deps)
@observer
export class SongInfoModal extends Component<SongInfoModalProps> {
  constructor(props) {
    super(props);
  }

  showEula(track: TrackSchema): void {
    this.props.controller.page.checkout.loadEulaInfo(track);
    this.props.controller.modal.showEulaModal(track);
  }

  handleTagClick(filterType: 'genre' | 'mood', tagSlug: string) {
    const {
      model: { router },
      controller: {
        page: {
          searchMvp: { refineFilter },
        },
      },
    } = this.props;

    const isSearchPage = router.location.pathname.startsWith('/search');

    if (isSearchPage) {
      // Get current search parameters
      const urlParams = new URLSearchParams(router.location.search);

      // Get existing genre and mood filters
      const existingTags = urlParams.get(filterType) ? urlParams.get(filterType).split(',') : [];

      // Update the filters based on the clicked tag
      if (!existingTags.includes(tagSlug)) {
        existingTags.push(tagSlug);
      }

      // Refine the search filters
      refineFilter(filterType, existingTags.join(','));
    } else {
      // Navigate to the search page with the filter applied
      const searchUrl = getGenreOrMoodHref(filterType, tagSlug);
      router.push(searchUrl);
    }

    this.props.onRequestClose();
  }

  handleBpmClick(track: TrackSchema) {
    const {
      model: { router },
      controller: {
        page: {
          searchMvp: { refineFilter },
        },
      },
    } = this.props;

    const isSearchPage = router.location.pathname.startsWith('/search');

    if (isSearchPage) {
      refineFilter('tempoRange', [track.bpm, track.bpm]);
    } else {
      // Navigate to the search page with the filter applied
      const searchUrl = getBpmHref(track);
      router.push(searchUrl);
    }

    this.props.onRequestClose();
  }

  handleTempoClick(track: TrackSchema) {
    const {
      model: { router },
      controller: {
        page: {
          searchMvp: { refineFilter },
        },
      },
    } = this.props;

    const isSearchPage = router.location.pathname.startsWith('/search');

    if (isSearchPage) {
      refineFilter('tempoRange', getTempoRange(track));
    } else {
      // Navigate to the search page with the filter applied
      const searchUrl = getTempoHref(track);
      router.push(searchUrl);
    }

    this.props.onRequestClose();
  }

  render() {
    const { show, track, onRequestClose } = this.props;

    let genres = null;
    let moods = null;
    let tempo = null;
    let bpm = null;

    let isBrandSponsoredTrack = null;
    let artistSlug = null;
    let releaseSlug = null;
    let isClaimsManaged = null;

    if (track) {
      if (track.artists.length > 0) {
        artistSlug = track.artists[0].slug;
      }
      if (track.releases.length > 0) {
        releaseSlug = track.releases[0].slug;
      }
      isBrandSponsoredTrack = track.brand_sponsored;

      genres = track.tags?.genre || [];
      moods = track.tags?.mood || [];

      tempo = maybe(() => track.tempo, null);
      bpm = maybe(() => track.bpm, null);

      isClaimsManaged = track.reporting_party.slug === 'umg';
    }

    if (isEmpty(track))
      return (
        <ScrollableOverlay show={show} onRequestClose={onRequestClose}>
          <div className={overlayStyles.header}>
            <h3 className={overlayStyles.title}>Song Information</h3>
          </div>

          <div className={styles.container}>
            <Loading className={styles.loading} />
          </div>
        </ScrollableOverlay>
      );

    return (
      <ScrollableOverlay show={show} onRequestClose={onRequestClose}>
        <div className={overlayStyles.header}>
          <h3 className={overlayStyles.title}>Song Information</h3>
        </div>

        <div className={styles.container}>
          <TrackFeatureCard track={track} hideTrackInfoBtn onClickLink={onRequestClose} />

          <div className={styles.contentScroll}>
            {track.artists && (
              <div className={styles.sectionContainer}>
                <h3 className={styles.subHeading}>Artists</h3>
                <div className={styles.artistList}>
                  <Person className={styles.personArtistList} size={16} />
                  <ArtistLinkList model={{ artists: track.artists }} linkClassName={styles.artistLinkList} />
                </div>
              </div>
            )}

            <div className={classes(styles.trackCharacteristicsContainer, styles.sectionContainer)}>
              <div className={styles.trackCharacteristic}>
                {genres && genres.length > 0 && (
                  <div>
                    <h3 className={styles.subHeading}>Genres</h3>
                    <div className={styles.trackTags}>
                      {genres.map((genre) => (
                        <TagButton
                          key={genre.slug}
                          tagStyle="small"
                          text={genre.name}
                          aria-label={`Search for tracks by genre: ${genre.name}`}
                          onClick={() => this.handleTagClick('genre', genre.slug)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {moods && moods.length > 0 && (
                  <div>
                    <h3 className={styles.subHeading}>Moods</h3>
                    <div className={styles.trackTags}>
                      {moods.map((mood) => (
                        <TagButton
                          key={mood.slug}
                          tagStyle="small"
                          text={mood.name}
                          aria-label={`Search for tracks by mood: ${mood.name}`}
                          onClick={() => this.handleTagClick('mood', mood.slug)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.trackCharacteristic}>
                {((tempo && tempo !== 'Unknown') || !!bpm) && (
                  <div>
                    <h3 className={styles.subHeading}>Tempo</h3>
                    <div className={styles.trackTags}>
                      {!!bpm && (
                        <TagButton
                          tagStyle="small"
                          onClick={() => this.handleBpmClick(track)}
                          text={`${track.bpm} BPM`}
                          aria-label={`Search for tracks by BPM: ${track.bpm}`}
                        />
                      )}
                      {!!tempo && (
                        <TagButton
                          tagStyle="small"
                          onClick={() => this.handleTempoClick(track)}
                          text={track.tempo}
                          aria-label={`Search for tracks by tempo: ${track.tempo}`}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* To be tested once the API is unpdated */}
            {track.is_instrumental && (
              <div className={classes(styles.sectionContainer, styles.noVocals)}>
                <NoVocal />
                <div className={styles.noVocalsText}>No vocals</div>
              </div>
            )}

            <div className={styles.sectionContainer}>
              <h3 className={styles.subHeading}>Target Platforms</h3>
              <ul className={styles.platforms}>
                {track.video_platforms &&
                  Object.entries(track.video_platforms).map(([name, allowed], index) => (
                    <VideoPlatform key={name} name={name} allowed={allowed} />
                  ))}
              </ul>

              <div className={styles.claimsManaged}>
                <h3 className={styles.subHeading}>{isClaimsManaged ? 'Manually ' : 'Automatically '} cleared claims</h3>

                <ClaimsManagedStatus track={track} />
              </div>
            </div>

            <div className={styles.sectionContainer}>
              <div className={styles.headingsArrowsContainer} onClick={() => this.showEula(track)}>
                <Description size={20} />
                <h3>View license agreement</h3>
                <ArrowRight className={styles.arrowBtn} />
              </div>
              {artistSlug && (
                <Link
                  className={styles.headingsArrowsContainer}
                  to={`/music/artists/${artistSlug}`}
                  onClick={onRequestClose}
                >
                  <Person size={20} />
                  <h3>Go to artist</h3>
                  <ArrowRight className={styles.arrowBtn} />
                </Link>
              )}
              {releaseSlug && (
                <Link
                  className={styles.headingsArrowsContainer}
                  to={`/music/artists/${artistSlug}/release/${releaseSlug}`}
                  onClick={onRequestClose}
                >
                  <Album size={20} />
                  <h3>Go to release</h3>
                  <ArrowRight className={styles.arrowBtn} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </ScrollableOverlay>
    );
  }
}

const styles = {
  loading: style({
    top: '40%',
    width: percent(50),
    marginLeft: 'auto',
    marginRight: 'auto',
  }),
  container: style({
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
  }),
  player: style({
    display: 'flex',
    flexShrink: 0,
    background: colorContrast100.toHexString(),
  }),
  contentScroll: style({
    overflowY: 'auto',
  }),
  subHeading: style({
    fontFamily: fontstackDefault,
    fontWeight: 700,
    fontSize: '16px',
    marginBottom: spacing.XSMALL,
  }),
  addRemoveTrackBtns: style(centerCenter, horizontal, horizontallySpaced(10), {
    padding: '0 10px 0 8px',
    marginLeft: 'auto',
    flexShrink: 0,
  }),
  addToPlaylistButton: style({ width: px(36), height: px(36) }),
  addToPlaylistTooltip: style({ zIndex: zModal }),
  artistList: style({
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: 0,
    marginBottom: '16px',
  }),
  personArtistList: style({
    marginTop: '4px',
    marginRight: '8px',
  }),
  artistLinkList: style({ color: colorGunmetal.toString(), $nest: { ...resetAnchorStyles(colorGunmetal.toString()) } }),
  notifyBtn: style({
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginLeft: 'auto',
    padding: ' 0 10px 0 8px',
  }),
  sectionContainer: style({
    padding: `${spacing.DEFAULT} 0`,
    borderBottom: `1px solid ${rebrand.neutralOnDark[20].toString()}`,
    $nest: {
      '&:last-child': {
        borderBottom: 0,
        paddingBottom: 0,
      },
    },
  }),
  trackTags: style(flexRoot, wrap, {
    gap: spacing.XXSMALL,
  }),
  trackCharacteristicsContainer: style({
    display: 'flex',
  }),
  trackCharacteristic: style({
    width: '50%',
    $nest: {
      '& > * + *': {
        marginTop: spacing.DEFAULT,
      },
    },
  }),
  platforms: style({
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.XXSMALL,
    padding: 0,
    margin: `0 0 ${spacing.DEFAULT}`,
  }),
  claimsManaged: style({
    backgroundColor: rebrand.neutralOnDark[10].toString(),
    padding: spacing.XSMALL,
    borderRadius: borderRadius.SMALL,
  }),
  headingsArrowsContainer: style({
    textDecoration: 'none',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.DEFAULT,
    border: `1px solid ${rebrand.neutralOnDark[20].toString()}`,
    $nest: {
      '&:not(:first-child)': {
        borderTop: 0,
      },
      '&:first-child': {
        borderTopLeftRadius: borderRadius.SMALL,
        borderTopRightRadius: borderRadius.SMALL,
      },
      '&:last-child': {
        borderBottomLeftRadius: borderRadius.SMALL,
        borderBottomRightRadius: borderRadius.SMALL,
      },
      '& h3': {
        fontFamily: fontstackDefault,
        fontWeight: 700,
        fontSize: px(16),
        margin: '0 auto 0 8px',
        color: colorGunmetal.toHexString(),
      },

      '&:hover': {
        cursor: 'pointer',
      },
    },
  }),
  arrowBtn: style({
    marginLeft: spacing.DEFAULT,
  }),
  noVocals: style({
    display: 'flex',
    fontSize: px(12),
    alignItems: 'center',
  }),
  noVocalsText: style({
    marginLeft: px(4),
  }),
};
